import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ROUTE_SEO } from "@/data/routeSeo";
import { ORG_ID, WEBSITE_ID, organizationSchema } from "@/data/schema";
import { SITE, canonicalFor, abs } from "@/lib/site";

const DEFAULT_IMAGE = `${SITE}/og-image.png`;

/**
 * Title, description, canonical, robots, Open Graph / Twitter tags and one
 * JSON-LD @graph per page. Titles and descriptions come from ROUTE_SEO for the
 * current path, falling back to props. react-helmet-async renders the tags in
 * the browser, and scripts/prerender.js writes them into each route's HTML.
 *
 * `schema` takes one schema.org object or an array; each is added to the page
 * graph next to the shared Organization and WebSite entities.
 */
export default function SEO({ title, description, path, image, type = "website", noindex = false, schema, breadcrumbs }) {
  const { pathname } = useLocation();
  const route = (path || pathname).split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  const preset = ROUTE_SEO[route] || {};
  const rawTitle = preset.title || title || "Orina Enterprise";
  const fullTitle = /Orina/.test(rawTitle) ? rawTitle : `${rawTitle} | Orina Enterprise`;
  const desc = preset.description || description;
  const url = canonicalFor(route);
  const img = abs(image) || DEFAULT_IMAGE;

  const graph = [
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: `${SITE}/`,
      name: "Orina Enterprise",
      inLanguage: "en",
      publisher: { "@id": ORG_ID },
    },
    organizationSchema,
  ];
  if (!noindex) {
    graph.push({
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: fullTitle,
      ...(desc && { description: desc }),
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": ORG_ID },
      inLanguage: "en",
    });
  }
  // Inner pages without an explicit trail get Home > this page.
  const trail = breadcrumbs || (route !== "/" ? [{ name: "Home", path: "/" }, { name: fullTitle.split(" | ")[0], path: route }] : null);
  if (!noindex && trail && trail.length > 0) {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: trail.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: canonicalFor(b.path),
      })),
    });
  }
  for (const s of [].concat(schema || [])) {
    const { "@context": _ctx, ...rest } = s;
    graph.push(rest);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      <meta
        name="robots"
        content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />
      {!noindex && <link rel="canonical" href={url} />}
      <meta property="og:site_name" content="Orina Enterprise" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      {!noindex && <meta property="og:url" content={url} />}
      <meta property="og:image" content={img} />
      {img === DEFAULT_IMAGE && <meta property="og:image:width" content="1200" />}
      {img === DEFAULT_IMAGE && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={fullTitle} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {desc && <meta name="twitter:description" content={desc} />}
      <meta name="twitter:image" content={img} />
      <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}</script>
    </Helmet>
  );
}
